/**
 * Pick a questionnaire header logo URL from client config.
 * Prefer the uploaded header/wordmark logo over legacy top-level logo_url so
 * every intake and checkout surface uses the same client asset.
 */
export function isLegacyPlaceholderLogoUrl(url) {
  if (!url || typeof url !== "string") return true;
  const normalized = url.replace(/\\/g, "/").toLowerCase();
  return (
    /static\/logo\.svg/i.test(normalized) ||
    normalized.endsWith("/assets/logo.png") ||
    normalized === "assets/logo.png"
  );
}

function logoStringCandidate(value) {
  if (typeof value !== "string") return "";
  const t = value.trim();
  return t;
}

/**
 * Program runtime responses may select the client logo used by the public
 * questionnaire. They must not replace the colour, typography, or theme
 * configured for that client in the questionnaire itself.
 */
export function logoOnlyBrandingOverride(branding) {
  if (!branding || typeof branding !== "object" || Array.isArray(branding)) {
    return null;
  }
  // Control-plane Admin previews are intentionally brand-neutral. Never show
  // client media there, even if an upstream payload is later extended.
  if (branding.branding_scope === "admin") return null;

  const logos = branding.logos;
  const hasLogos = (
    logos
    && typeof logos === "object"
    && !Array.isArray(logos)
    && Object.values(logos).some((value) => logoStringCandidate(value))
  );
  const logoUrl = logoStringCandidate(branding.logo_url);
  const companyName = logoStringCandidate(branding.company_name);

  if (!hasLogos && !logoUrl && !companyName) return null;

  return {
    ...(hasLogos ? { logos: { ...logos } } : {}),
    ...(logoUrl ? { logo_url: logoUrl } : {}),
    ...(companyName ? { company_name: companyName } : {}),
  };
}

/**
 * Programs and Custom Programs use the shared WellieMD questionnaire UI.
 * Their assigned client can supply its logo and display name, but not a competing colour, type,
 * or theme override. Legacy questionnaires retain their configured branding.
 */
export function resolveQuestionnaireBranding({
  baseBranding,
  runtimeBranding,
  useWellieMdTheme = false,
} = {}) {
  const base = useWellieMdTheme
    ? logoOnlyBrandingOverride(baseBranding)
    : baseBranding;
  const runtime = logoOnlyBrandingOverride(runtimeBranding);

  if (!base) return runtime || undefined;
  if (!runtime) return base;
  return { ...base, ...runtime };
}

export function pickQuestionnaireLogoUrl(questionnaireConfig) {
  if (!questionnaireConfig || typeof questionnaireConfig !== "object") return "";
  const { logos, logo_url: topLogo } = questionnaireConfig;
  const ordered = [];
  if (logos && typeof logos === "object" && !Array.isArray(logos)) {
    for (const key of ["header_logo", "transparent", "square", "round", "favicon"]) {
      const v = logoStringCandidate(logos[key]);
      if (v) ordered.push(v);
    }
    for (const k of Object.keys(logos)) {
      if (["header_logo", "transparent", "square", "round", "favicon"].includes(k)) continue;
      const v = logoStringCandidate(logos[k]);
      if (v) ordered.push(v);
    }
  }
  const top = logoStringCandidate(topLogo);
  if (top) ordered.push(top);
  for (const c of ordered) {
    if (!isLegacyPlaceholderLogoUrl(c)) return c;
  }
  return "";
}
